import { useTeamCatalog, withTeamCatalog } from '@context/TeamCatalog.context'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import moment from 'moment'
import {
  Badge,
  Button,
  DataTable,
  type DataTableColumn,
} from '@enamelai/design-system'
import { Provider } from '@dataTypes/provider.types'
import { getFullName } from '@utils/fullname.util'
import { WorkStatus, isProviderSeatRole } from '@constants/Providers'
import { useProviderActions } from '@components/pages/Provider/hooks/useProviderActions'
import { DeleteConfirmationModal } from '@components/layout/Modals'
import { getAllStaffs, getProviderSeatLimit } from '@services/api/provider.api'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import { useTheme } from '@context/ThemeContext'
import { useSettingsHeader } from '@pages/Settings/SettingsHeader.context'
import { useSettingsSubpageLoading } from '@pages/Settings/SettingsSubpageGate.component'
import { MOCK_PROVIDERS } from '@assets/mockdata/Provider/mockProviders.data'
import { MOCK_CLINICS } from '@assets/mockdata/Clinic/mockClinics.data'
import { getEnv } from '@utils/env'
import { fetchAllClinics } from '@store/actions'
import SettingsRowActions from '../../shared/SettingsRowActions.component'
import {
  sortRows,
  usePagedRows,
  useTableSort,
} from '../../shared/settingsTable'
import { cellForDay } from '../WorkSchedule/staffShifts.util'
import AddStaff from './AddStaff/AddStaff.component'
import StaffOverviewCard from './StaffOverviewCard.component'
import {
  TEAM_BADGE_COLOR,
  WORK_STATUS_BADGE_COLORS,
  teamForRole,
} from './StaffListHelper'
import './StaffListStyle.css'

const EMPTY_CELL = '-'

/** The Figma omits the overview card; flip to true to show it again. */
const SHOW_STAFF_OVERVIEW = false

type StaffRow = {
  id: string
  name: string
  role: string
  team: string
  email: string
  ext: string
  shift: string
  status: string
  lastLogin: string
  lastLoginSort: string
}

const formatLastLogin = (raw?: string): string => {
  if (!raw) return EMPTY_CELL
  const parsed = moment(raw)
  return parsed.isValid() ? parsed.format('MM/DD/YYYY - hh:mm A') : EMPTY_CELL
}

const todaysShift = (staff: Provider): string => {
  const cell = cellForDay(staff, moment())
  if (cell.overrideLabel) return cell.overrideLabel
  return cell.kind === 'shift' ? cell.label : EMPTY_CELL
}

const readSortValue = (row: StaffRow, columnId: string): string =>
  columnId === 'lastLogin'
    ? row.lastLoginSort
    : String(row[columnId as keyof StaffRow] ?? '').toLowerCase()

type StaffListTableProps = {
  refreshKey?: number
  onAddStaff?: () => void
}

const StaffListTable: React.FC<StaffListTableProps> = ({
  refreshKey = 0,
  onAddStaff,
}) => {
  const catalog = useTeamCatalog()
  const { setHeaderButtons } = useSettingsHeader()
  const { resolvedTheme } = useTheme()
  const badgeMode = resolvedTheme === 'dark' ? 'solid' : 'tinted'
  const useSettingsMocks = getEnv('REACT_APP_USE_SETTINGS_MOCKS', '') === 'true'

  const [staffRows, setStaffRows] = useState<Provider[]>([])
  const [loading, setLoading] = useState<boolean>(true)
  const [providerSeatLimit, setProviderSeatLimit] = useState<number | null>(
    null,
  )
  const [seatLimitLoading, setSeatLimitLoading] = useState<boolean>(true)
  const [sort, toggleSort] = useTableSort('name')

  const loadStaff = useCallback(async (): Promise<void> => {
    setLoading(true)
    setSeatLimitLoading(true)
    try {
      const [staffRes, seatRes] = await Promise.all([
        getAllStaffs(),
        getProviderSeatLimit(),
      ])
      if (staffRes.statusCode === 200 && staffRes.data?.staffs) {
        setStaffRows(staffRes.data.staffs)
      } else {
        setStaffRows([])
      }
      if (
        seatRes.statusCode === 200 &&
        seatRes.data &&
        typeof seatRes.data.provider_limit === 'number'
      ) {
        setProviderSeatLimit(seatRes.data.provider_limit)
      } else {
        setProviderSeatLimit(null)
      }
    } catch {
      setStaffRows([])
      setProviderSeatLimit(null)
    } finally {
      setLoading(false)
      setSeatLimitLoading(false)
    }
  }, [])

  useSettingsSubpageLoading(loading || seatLimitLoading)

  useEffect(() => {
    void loadStaff()
  }, [loadStaff, refreshKey])

  const staffs = useMemo(() => {
    if (staffRows.length > 0) return staffRows
    if (!useSettingsMocks) return []

    const mockClinicIds = MOCK_CLINICS.map((c) => c.clinic_id)
    const fallbackClinicId = mockClinicIds[0] ?? 'mock-clinic-1'

    return MOCK_PROVIDERS.map((p, idx) => ({
      ...p,
      clinic_id: mockClinicIds[idx % mockClinicIds.length] ?? fallbackClinicId,
    }))
  }, [staffRows, useSettingsMocks])

  const staffOverviewStats = useMemo(() => {
    let activeStaffCount = 0
    let activeProviderCount = 0
    for (const s of staffs) {
      if ((s.work_status || '') !== WorkStatus.ACTIVE) continue
      activeStaffCount += 1
      if (isProviderSeatRole(s.work_role)) activeProviderCount += 1
    }
    return {
      totalStaffCount: staffs.length,
      activeStaffCount,
      activeProviderCount,
      activeNonProviderCount: activeStaffCount - activeProviderCount,
    }
  }, [staffs])

  const providerActionCallbacks = useMemo(
    () => ({ onAfterDeleteSuccess: loadStaff }),
    [loadStaff],
  )

  const {
    handleEditProvider,
    handleDeleteProvider,
    confirmDeleteProvider,
    handleCloseProviderModal,
    handleCloseConfirmDeleteModal,
    modalState,
  } = useProviderActions(providerActionCallbacks)

  const hasNoStaff = staffs.length === 0

  useEffect(() => {
    setHeaderButtons(
      onAddStaff && !hasNoStaff ? (
        <Button
          className="settings-ds-button"
          variant="primary"
          size="md"
          type="button"
          onClick={onAddStaff}
        >
          Add staff
        </Button>
      ) : null,
    )
    return () => setHeaderButtons(null)
  }, [hasNoStaff, onAddStaff, setHeaderButtons])

  const staffById = useMemo(
    () => new Map(staffs.map((s) => [s.provider_id, s])),
    [staffs],
  )

  const rows: StaffRow[] = useMemo(
    () =>
      staffs.map((s) => ({
        id: s.provider_id,
        name: getFullName(s) || s.username || EMPTY_CELL,
        role: s.work_role || EMPTY_CELL,
        team: s.team ?? teamForRole(s.work_role, catalog),
        email: s.email || EMPTY_CELL,
        ext: s.work_contact || EMPTY_CELL,
        shift: todaysShift(s),
        status: s.work_status || '',
        lastLogin: formatLastLogin(s.last_login),
        lastLoginSort: s.last_login ? moment(s.last_login).toISOString() : '',
      })),
    [staffs, catalog],
  )

  const sortedRows = useMemo(
    () => sortRows(rows, sort, readSortValue),
    [rows, sort],
  )
  const { pageRows, pagination } = usePagedRows(sortedRows, {
    alwaysShow: true,
  })

  const columns: DataTableColumn<StaffRow>[] = useMemo(
    () => [
      { id: 'name', label: 'Name', sortable: true },
      { id: 'role', label: 'Role', sortable: true },
      {
        id: 'team',
        label: 'Team',
        sortable: true,
        showInfo: true,
        width: 120,
        renderCell: (row) =>
          row.team ? (
            <Badge mode={badgeMode} color={TEAM_BADGE_COLOR} label={row.team} />
          ) : (
            EMPTY_CELL
          ),
      },
      { id: 'email', label: 'Email', sortable: true },
      { id: 'ext', label: 'Ext.', sortable: true, width: 80 },
      { id: 'shift', label: 'Today’s shift', sortable: true, width: 148 },
      {
        id: 'status',
        label: 'Status',
        sortable: true,
        showInfo: true,
        width: 136,
        renderCell: (row) =>
          row.status ? (
            <Badge
              mode={badgeMode}
              color={WORK_STATUS_BADGE_COLORS[row.status] ?? 'gray'}
              label={row.status}
            />
          ) : (
            EMPTY_CELL
          ),
      },
      { id: 'lastLogin', label: 'Last login', sortable: true, width: 184 },
    ],
    [badgeMode],
  )

  const tableState = loading ? 'loading' : hasNoStaff ? 'empty' : 'default'

  return (
    <>
      {SHOW_STAFF_OVERVIEW && !hasNoStaff && (
        <StaffOverviewCard
          {...staffOverviewStats}
          providerSeatLimit={providerSeatLimit}
          limitLoading={seatLimitLoading}
        />
      )}
      <DataTable<StaffRow>
        className="sl-table"
        columns={columns}
        rows={pageRows}
        state={tableState}
        emptyState={{
          title: 'No staff found',
          description: "You don't have any staff on record yet.",
          actionLabel: onAddStaff ? 'Add staff' : undefined,
          onAction: onAddStaff,
        }}
        sortColumnId={sort.columnId}
        sortDirection={sort.columnId ? sort.direction : undefined}
        onSort={toggleSort}
        actionsLabel="Action"
        renderActions={(row) => {
          const staff = staffById.get(row.id)
          return (
            <SettingsRowActions
              disabled={!staff}
              onEdit={() => staff && handleEditProvider(staff)}
              onDelete={() => staff && handleDeleteProvider(staff)}
            />
          )
        }}
        pagination={pagination}
      />

      <AddStaff
        isOpen={modalState.showProviderModal && !!modalState.selectedProvider}
        staffToEdit={modalState.selectedProvider}
        onRequestClose={handleCloseProviderModal}
        onSaved={() => void loadStaff()}
      />

      <DeleteConfirmationModal
        show={modalState.showConfirmDeleteModal}
        onHide={handleCloseConfirmDeleteModal}
        onConfirm={() =>
          modalState.providerToDelete &&
          confirmDeleteProvider(modalState.providerToDelete)
        }
        entityName={
          modalState.providerToDelete
            ? getFullName(modalState.providerToDelete)
            : ''
        }
      />
    </>
  )
}

const StaffList = (): React.ReactElement => {
  const dispatch = useAppDispatch()
  const isAuthenticated = useAppSelector((s) => s.auth.isAuthenticated)
  const clinics = useAppSelector((s) => s.clinic.data)
  const clinicLoading = useAppSelector((s) => s.clinic.loading)
  const emptyFetchDoneRef = useRef(false)

  const [addStaffOpen, setAddStaffOpen] = useState(false)
  const [staffTableRefreshKey, setStaffTableRefreshKey] = useState(0)

  useEffect(() => {
    if (!isAuthenticated) {
      emptyFetchDoneRef.current = false
      return
    }
    if (clinics.length > 0) {
      emptyFetchDoneRef.current = false
      return
    }
    if (clinicLoading || emptyFetchDoneRef.current) {
      return
    }
    emptyFetchDoneRef.current = true
    void dispatch(fetchAllClinics())
  }, [isAuthenticated, clinics.length, clinicLoading, dispatch])

  useEffect(() => {
    return () => {
      emptyFetchDoneRef.current = false
    }
  }, [])

  const openAddStaff = useCallback((): void => {
    setAddStaffOpen(true)
  }, [])

  const closeAddStaff = useCallback((): void => {
    setAddStaffOpen(false)
  }, [])

  return (
    <div className="staff-list-settings" id="scp-staff-list">
      <StaffListTable
        refreshKey={staffTableRefreshKey}
        onAddStaff={openAddStaff}
      />
      <AddStaff
        isOpen={addStaffOpen}
        onRequestClose={closeAddStaff}
        onSaved={() => setStaffTableRefreshKey((k) => k + 1)}
      />
    </div>
  )
}

export default withTeamCatalog(StaffList)
